import { Observable, Subject, from, of, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { IInit } from '../../../common/container/Container'
import { Container } from '../../../common/container/Container'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { NotificationContainerConfig } from '../container'
import { GotifyMessage } from '../models/GotifyMessage'
import { t } from 'i18next'

export interface IGotifyMessageApi extends IInit {
  connect(token: string): Observable<boolean>
  disconnect(): void
  getMessages(): Observable<GotifyMessage>
  requestNotificationPermission(): Observable<boolean>
  readonly connected: boolean
  readonly notificationPermission: NotificationPermission
}

export class GotifyMessageApi implements IGotifyMessageApi {
  private _container!: Container
  private _statusService!: IStatusService
  private _url!: string
  private _ws: WebSocket | null = null
  private _messageSubject = new Subject<GotifyMessage>()
  private _connected = false
  private _notificationPermission: NotificationPermission = 'default'

  init(c: Container) {
    this._container = c
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)

    const config = this._container.config as NotificationContainerConfig
    this._url = config.gotifyUrl

    // Verificar soporte para notificaciones al inicializar
    if ('Notification' in window) {
      this._notificationPermission = Notification.permission
    }
  }

  get connected(): boolean {
    return this._connected
  }

  get notificationPermission(): NotificationPermission {
    return this._notificationPermission
  }

  connect(token: string): Observable<boolean> {
    if (!token) {
      this._statusService.sendStatus({
        variant: 'error',
        message: 'Token no disponible. No se puede conectar.',
      })
      return throwError(() => new Error('Token no disponible'))
    }
    let wsProtocol = 'ws'
    if (this._url.startsWith('https://')) {
      wsProtocol = 'wss'
    } else if (this._url.startsWith('http://')) {
      wsProtocol = 'ws'
    } else {
      this._statusService.sendStatus({
        variant: 'error',
        message: 'URL inválida. Debe comenzar con http:// o https://',
      })
      return throwError(() => new Error('URL inválida'))
    }

    const cleanedUrl = this._url.replace(/^https?:\/\//, '')
    const wsUrl = `${wsProtocol}://${cleanedUrl}/stream?token=${token}`

    return new Observable<boolean>((subscriber) => {
      this._ws = new WebSocket(wsUrl)

      this._ws.onopen = () => {
        this._connected = true
        subscriber.next(true)
        subscriber.complete()
        this._statusService.sendStatus({
          variant: 'success',
          message: 'Conexión WebSocket establecida',
        })
      }

      this._ws.onmessage = (event) => {
        try {
          const msg: GotifyMessage = JSON.parse(event.data)
          this._messageSubject.next(msg)
          this._showPushNotification(msg)
        } catch (err) {
          this._statusService.sendStatus({
            variant: 'error',
            message: 'Error al procesar mensaje WebSocket',
          })
        }
      }

      this._ws.onerror = (error) => {
        this._connected = false
        this._statusService.sendStatus({
          variant: 'error',
          message: 'Error en conexión WebSocket',
        })
        subscriber.error(error)
      }

      this._ws.onclose = (event) => {
        this._connected = false
        if (!event.wasClean && event.reason) {
          this._statusService.sendStatus({
            variant: 'warning',
            message: `Conexión WebSocket cerrada inesperadamente: ${event.reason || 'Sin razón'}`,
          })
        }
      }
    }).pipe(
      catchError((error) => {
        this._statusService.sendStatus({
          variant: 'error',
          error: error,
        })
        return throwError(() => error)
      })
    )
  }

  disconnect(): void {
    if (this._ws) {
      if (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING) {
        this._ws.close()
      }
      this._ws = null
      this._connected = false
    }
  }

  getMessages(): Observable<GotifyMessage> {
    return this._messageSubject.asObservable()
  }

  requestNotificationPermission(): Observable<boolean> {
    if (!('Notification' in window)) {
      this._statusService.sendStatus({
        variant: 'warning',
        message: 'Este navegador no soporta notificaciones',
      })
      return of(false)
    }

    if (Notification.permission === 'granted') {
      this._notificationPermission = 'granted'
      return of(true)
    }

    if (Notification.permission === 'denied') {
      this._notificationPermission = 'denied'
      this._statusService.sendStatus({
        variant: 'warning',
        message: 'Las notificaciones están bloqueadas',
      })
      return of(false)
    }

    return from(Notification.requestPermission()).pipe(
      map((permission) => {
        this._notificationPermission = permission
        return permission === 'granted'
      }),
      catchError((error) => {
        this._statusService.sendStatus({
          variant: 'error',
          error: error,
          message: 'Error al solicitar permisos de notificación',
        })
        return of(false)
      })
    )
  }

  private _showPushNotification(message: GotifyMessage): void {
    this.requestNotificationPermission().subscribe((hasPermission) => {
      if (!hasPermission) return

      try {
        const preview =
          message.message.length > 100 ? message.message.substring(0, 97) + '...' : message.message

        const notification = new Notification(message.title || 'Nuevo mensaje', {
          body: preview,
          tag: `gotify-${message.id}`,
          data: {
            messageId: message.id,
            fullMessage: message.message,
          },
          requireInteraction: message.priority >= 5,
          silent: message.priority <= 2,
        })

        notification.onclick = (event) => {
          event.preventDefault()
          window.focus()
          notification.close()
        }

        setTimeout(() => notification.close(), 10000)
      } catch (error) {
        this._statusService.sendStatus({
          variant: 'error',
          message: t('anErrorOccurred'),
        })
      }
    })
  }
}
